import { configure } from 'mobx'
import {
  applySnapshot,
  Instance,
  onPatch,
  SnapshotIn,
  SnapshotOut,
  types,
  unprotect
} from 'mobx-state-tree'
import { useMemo } from 'react'
import { ForDirectExtend } from '../../features/mobx'
import { AlternatePlayerStore } from './AlternatePlayerStore'
import { GlobalPlayerStore } from './GlobalPlayerStore'
import { PlayerStore } from './PlayerStore'
import { deserializeSongFromStorage } from './song/SongStore'
import { IStudioStore, StudioStore } from './StudioStore'

configure({
  enforceActions: 'never'
})

let store

const _Root = types
  .model({
    studio: types.maybeNull(StudioStore),
    globalPlayer: types.maybeNull(GlobalPlayerStore),
    player: types.maybe(PlayerStore),
    // Use this if you need to render 2 players withing the same view
    alternatePlayer: types.maybe(AlternatePlayerStore),
    isStudioPlaying: false,
    appState: types.optional(types.string, 'active'),
    hotkeysDisabled: false
  })
  .actions((self) => ({
    async afterCreate() {
      unprotect(self)
    },
    activeInstance(): IStudioStore {
      if (self.studio.playhead > 0) return self.studio
      if (self.player.playhead > 0) return self.player
      if (self.alternatePlayer.playhead > 0) return self.alternatePlayer
    },
    async initRoot() {
      // Don't reinitialize studio if we loaded this from local storage.
      if (self.studio === null) {
        self.studio = StudioStore.create()
        self.studio.resetPlayhead()
        self.studio.registerUndoManager()
      }
      self.globalPlayer = GlobalPlayerStore.create()
      self.player = PlayerStore.create()
      self.alternatePlayer = AlternatePlayerStore.create()
    },
    updateAppState(appState) {
      self.appState = appState
      console.log('AppState', self.appState)
    },
    disableHotkeys(flag: boolean) {
      self.hotkeysDisabled = flag
    }
    // TODO reenable if necessary
    // stopActiveInstance(){
    //   console.log('activeInstance', this.activeInstance())
    //   if(this.activeInstance()) {
    //     this.activeInstance().stop()
    //   }
    // }
  }))

interface RootType extends ForDirectExtend<typeof _Root> {}
export const Root: RootType = _Root

let initialState: IStore = Root.create()

// Get the song from local storage
if (typeof Storage !== 'undefined') {
  try {
    const data = localStorage.getItem('songState')
    if (data) {
      const json = JSON.parse(data)
      if (!initialState.studio) {
        initialState.studio = StudioStore.create({
          song: deserializeSongFromStorage(json)
          // TODO add settings here as well.
        })
      }
    }
  } catch (error) {
    console.error(error)
    console.log('local storage unavailable')
  }
}

export const root = initialState

export interface IStore extends Instance<typeof Root> {}
export interface IStoreSnapshotIn extends SnapshotIn<typeof Root> {}
export interface IStoreSnapshotOut extends SnapshotOut<typeof Root> {}

// Initializes the entire state tree.
export function initializeStore(snapshot = null) {
  // Hydrate stores
  if (snapshot !== null) {
    applySnapshot(initialState, snapshot)
  }
  if (typeof window === 'undefined') return initialState
  // Create the store if it doesn't exist on the client side
  if (!store) {
    store = initialState
    store.initRoot()
  }

  return store
}

export function useTreeState(initialState = null): IStore {
  const store = useMemo(() => initializeStore(initialState), [initialState])
  return store as IStore
}

onPatch(root, (patch) => {
  if (patch.path.startsWith('/studio/song/') && root.studio.updateAudio) {
    root.studio.saveSongToLocalStorage()
  }
})
